import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useServiceOS } from '../hooks/useServiceOS';
import {
  Plus,
  MagnifyingGlass,
  FunnelSimple,
  Clock,
  EnvelopeSimple,
  Phone,
  MapPin,
} from '@phosphor-icons/react';

const STATUS_TABS = [
  { value: '', label: 'All' },
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'awaiting_reply', label: 'Awaiting' },
  { value: 'booked', label: 'Booked' },
  { value: 'lost', label: 'Lost' },
];

const STATUS_COLORS = {
  new: 'bg-[#14F195]/10 text-[#14F195]',
  contacted: 'bg-[#6366F1]/10 text-[#6366F1]',
  awaiting_reply: 'bg-[#F59E0B]/10 text-[#F59E0B]',
  booked: 'bg-[#22C55E]/10 text-[#22C55E]',
  lost: 'bg-[#EF4444]/10 text-[#EF4444]',
};

const SOURCE_COLORS = {
  email: 'bg-[#3B82F6]/10 text-[#3B82F6]',
  manual: 'bg-[#8B5CF6]/10 text-[#8B5CF6]',
  test: 'bg-[#6B7280]/10 text-[#6B7280]',
  website: 'bg-[#F97316]/10 text-[#F97316]',
};

function NewLeadModal({ open, onClose, onCreate }) {
  const [form, setForm] = useState({ raw_enquiry_text: '', customer_name: '', customer_email: '', customer_phone: '' });
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onCreate({ ...form, source_type: 'manual' });
      onClose();
      setForm({ raw_enquiry_text: '', customer_name: '', customer_email: '', customer_phone: '' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="new-lead-modal">
      <div className="bg-[#12131A] border border-[#1E1F2E] rounded-xl w-full max-w-md p-6">
        <h2 className="text-lg font-semibold text-white mb-4">New lead</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs text-[#8B8FA3] mb-1">Enquiry text *</label>
            <textarea
              rows={3}
              value={form.raw_enquiry_text}
              onChange={e => setForm(f => ({ ...f, raw_enquiry_text: e.target.value }))}
              className="w-full px-3 py-2 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg text-sm text-white focus:outline-none focus:border-[#14F195]/50"
              placeholder="Paste the customer's message or describe their enquiry..."
              required
              data-testid="new-lead-enquiry"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-[#8B8FA3] mb-1">Name</label>
              <input
                value={form.customer_name}
                onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))}
                className="w-full px-3 py-2 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg text-sm text-white focus:outline-none focus:border-[#14F195]/50"
                data-testid="new-lead-name"
              />
            </div>
            <div>
              <label className="block text-xs text-[#8B8FA3] mb-1">Email</label>
              <input
                type="email"
                value={form.customer_email}
                onChange={e => setForm(f => ({ ...f, customer_email: e.target.value }))}
                className="w-full px-3 py-2 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg text-sm text-white focus:outline-none focus:border-[#14F195]/50"
                data-testid="new-lead-email"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-[#8B8FA3] mb-1">Phone</label>
            <input
              value={form.customer_phone}
              onChange={e => setForm(f => ({ ...f, customer_phone: e.target.value }))}
              className="w-full px-3 py-2 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg text-sm text-white focus:outline-none focus:border-[#14F195]/50"
              data-testid="new-lead-phone"
            />
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-[#8B8FA3] hover:text-white">Cancel</button>
            <button
              type="submit"
              disabled={loading || !form.raw_enquiry_text.trim()}
              className="px-5 py-2 bg-[#14F195] text-[#0B0C10] rounded-lg text-sm font-semibold hover:bg-[#12D984] disabled:opacity-40"
              data-testid="new-lead-submit"
            >
              {loading ? 'Creating...' : 'Create lead'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function SOSLeads() {
  const { getLeads, createLead } = useServiceOS();
  const [searchParams, setSearchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const statusFilter = searchParams.get('status') || '';

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getLeads({ status: statusFilter || undefined });
      setLeads(res.leads);
      setTotal(res.total);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [getLeads, statusFilter]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const handleCreate = async (data) => {
    await createLead(data);
    fetchLeads();
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="sos-leads-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white">Leads</h1>
          <p className="text-sm text-[#8B8FA3]">{total} total</p>
        </div>
        <button
          onClick={() => setShowNew(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#14F195] text-[#0B0C10] rounded-lg text-sm font-semibold hover:bg-[#12D984] transition-colors"
          data-testid="new-lead-btn"
        >
          <Plus size={16} weight="bold" /> New lead
        </button>
      </div>

      {/* Status tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1" data-testid="leads-status-tabs">
        {STATUS_TABS.map(tab => (
          <button
            key={tab.value}
            onClick={() => setSearchParams(tab.value ? { status: tab.value } : {})}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
              statusFilter === tab.value
                ? 'bg-[#14F195]/10 text-[#14F195] border border-[#14F195]/30'
                : 'text-[#8B8FA3] hover:text-white border border-[#1E1F2E]'
            }`}
            data-testid={`leads-tab-${tab.value || 'all'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Lead list */}
      {loading ? (
        <div className="flex justify-center py-16" data-testid="leads-loading">
          <div className="w-5 h-5 border-2 border-[#14F195] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : leads.length === 0 ? (
        <div className="text-center py-16 bg-[#12131A] border border-[#1E1F2E] rounded-xl" data-testid="leads-empty">
          <p className="text-[#8B8FA3]">No leads{statusFilter ? ` with status "${statusFilter}"` : ''}</p>
          <button onClick={() => setShowNew(true)} className="mt-3 text-sm text-[#14F195] hover:underline">
            Create your first lead
          </button>
        </div>
      ) : (
        <div className="space-y-2" data-testid="leads-list">
          {leads.map(lead => (
            <Link
              key={lead.id}
              to={`/service-os/leads/${lead.id}`}
              className="block bg-[#12131A] border border-[#1E1F2E] rounded-xl p-4 hover:border-[#2E2F3E] transition-colors"
              data-testid={`lead-card-${lead.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-semibold text-white truncate">
                      {lead.customer_name || 'Unknown'}
                    </h3>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${STATUS_COLORS[lead.status] || ''}`}>
                      {lead.status}
                    </span>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${SOURCE_COLORS[lead.source_type] || 'bg-[#1E1F2E] text-[#8B8FA3]'}`} data-testid={`lead-source-${lead.id}`}>
                      {lead.source_type}
                    </span>
                  </div>
                  <p className="text-xs text-[#8B8FA3] line-clamp-1 mb-2">
                    {lead.enquiry_summary || lead.raw_enquiry_text?.slice(0, 100)}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-[#555]">
                    {lead.customer_email && (
                      <span className="flex items-center gap-1"><EnvelopeSimple size={12} /> {lead.customer_email}</span>
                    )}
                    {lead.customer_phone && (
                      <span className="flex items-center gap-1"><Phone size={12} /> {lead.customer_phone}</span>
                    )}
                    {lead.location_text && (
                      <span className="flex items-center gap-1"><MapPin size={12} /> {lead.location_text}</span>
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs text-[#555]">{new Date(lead.created_at).toLocaleDateString()}</p>
                  {lead.service_type && <p className="text-xs text-[#8B8FA3] mt-1">{lead.service_type}</p>}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      <NewLeadModal open={showNew} onClose={() => setShowNew(false)} onCreate={handleCreate} />
    </div>
  );
}
